'use client';
import { LogLevel, StatsigProvider, useClientBootstrapInit, StatsigUser } from '@statsig/react-bindings';
import { useEffect } from 'react';
import { StatsigSessionReplayPlugin } from '@statsig/session-replay';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';
import { useCookies } from 'react-cookie';

export default function MyStatsig({
  children,
  values,
  statsigUser
}: {
  values: string;
  children: React.ReactNode;
  statsigUser: StatsigUser & { _generatedStableId?: string };
}) {
  const [cookies, setCookie] = useCookies(['stableId']);

  // Handle server-generated stableID
  let finalStableId = statsigUser?.customIDs?.stableID;

  // If server generated a new stableID, set it in cookie immediately
  if (statsigUser?._generatedStableId && !cookies.stableId) {
    const domain =
      typeof window !== 'undefined'
        ? window.location.hostname === 'localhost'
          ? 'localhost'
          : `.${window.location.hostname.split('.').slice(-2).join('.')}`
        : undefined;

    console.log('StatsigProvider: Setting server-generated stableID in cookie:', statsigUser._generatedStableId);

    if (domain) {
      setCookie('stableId', statsigUser._generatedStableId, {
        path: '/',
        expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
        domain: domain,
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'lax'
      });
    }

    finalStableId = statsigUser._generatedStableId;
  } else if (cookies.stableId) {
    // Use existing cookie value
    finalStableId = cookies.stableId;
  }

  // Ensure we have a proper user object for client initialization
  const normalizedStatsigUser = {
    userID: statsigUser?.userID || undefined,
    email: statsigUser?.email || undefined,
    custom: statsigUser?.custom || {},
    customIDs: {
      ...statsigUser?.customIDs,
      // Use the final determined stableID
      stableID: finalStableId
    }
  };

  console.log('StatsigProvider: Initializing client with user:', {
    userID: normalizedStatsigUser.userID,
    email: normalizedStatsigUser.email,
    serverStableId: statsigUser?.customIDs?.stableID,
    generatedStableId: statsigUser?._generatedStableId,
    cookieStableId: cookies.stableId,
    finalStableId: finalStableId,
    hasBootstrapValues: !!values && values.length > 0,
    bootstrapValuesLength: values?.length || 0
  });

  const client = useClientBootstrapInit(process.env.NEXT_PUBLIC_STATSIG_CLIENT_KEY!, normalizedStatsigUser, values, {
    logLevel: LogLevel.Debug,
    plugins: [new StatsigAutoCapturePlugin(), new StatsigSessionReplayPlugin()],
    networkConfig: {
      preventAllNetworkTraffic: typeof window === 'undefined'
    },
    disableStorage: typeof window === 'undefined',
    environment: {
      tier: process.env.NEXT_PUBLIC_VERCEL_ENV === 'production' ? 'production' : 'development'
    }
  });

  useEffect(() => {
    try {
      if (!client) return;

      const context = client.getContext();
      const clientStableId = context?.stableID;

      console.log('StatsigProvider: Client context update:', {
        clientStableId,
        cookieStableId: cookies.stableId,
        serverStableId: statsigUser?.customIDs?.stableID,
        generatedStableId: statsigUser?._generatedStableId,
        finalStableId: finalStableId,
        contextKeys: context ? Object.keys(context) : [],
        isReady: client.loadingStatus === 'Ready'
      });

      // Verify exposure tracking is working
      if (client.loadingStatus === 'Ready') {
        console.log('StatsigProvider: Client is ready for exposure tracking');
      }

      // Verify stableID consistency
      if (clientStableId && finalStableId && clientStableId !== finalStableId) {
        console.warn('StatsigProvider: StableID mismatch detected!', {
          clientStableId: clientStableId,
          expectedStableId: finalStableId,
          serverStableId: statsigUser?.customIDs?.stableID,
          reason: 'This could cause exposure imbalance issues'
        });
      } else if (clientStableId && finalStableId && clientStableId === finalStableId) {
        console.log('StatsigProvider: StableID consistency confirmed:', clientStableId);
      }
    } catch (error) {
      console.error('StatsigProvider: Error handling stableId:', error);
    }
  }, [client, cookies.stableId, finalStableId, statsigUser?.customIDs?.stableID, statsigUser?._generatedStableId]);

  return <StatsigProvider client={client}>{children}</StatsigProvider>;
}
